import { graphql } from 'react-relay';
import { useMemo, useState } from 'react';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { ExportPolicyEnum } from '../ExportPolicyEnum.ts';

import type {
	useExportEnabledStateIsExportEnabledQuery as useExportEnabledStateIsExportEnabledQuery$data,
	useExportEnabledStateIsExportEnabledQueryVariables as useExportEnabledStateIsExportEnabledQuery$variables,
} from './__types__/useExportEnabledStateIsExportEnabledQuery.ts';
export const useExportEnabledState = (spaceId?: string | null) => {
	const [exportPolicyState, setExportPolicyState] = useState<ExportPolicyEnum>(
		ExportPolicyEnum.LOADING,
	);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const skipQuery = !spaceId || Boolean(process.env.REACT_SSR); // IsExportEnabledQuery isn't preloaded and can be skipped during SSR to avoid unnecessary requests
	const { data, loading, error } = useQueryInterop<
		useExportEnabledStateIsExportEnabledQuery$data,
		useExportEnabledStateIsExportEnabledQuery$variables
	>(
		graphql`
			query useExportEnabledStateIsExportEnabledQuery($spaceId: ID!) {
				space(id: $spaceId) {
					id
					isExportEnabled
				}
			}
		`,
		{
			variables: { spaceId: spaceId || '' },
			skip: skipQuery,
			errorPolicy: 'all',
			fetchPolicy: 'no-cache',
		},
	);

	useMemo(() => {
		const isExportEnabled = data?.space?.isExportEnabled;
		if (error) {
			if (error?.message.includes('DataExportPolicyCheckException')) {
				markErrorAsHandled(error);
			}
			setExportPolicyState(ExportPolicyEnum.ERROR);
		} else if (loading) {
			setExportPolicyState(ExportPolicyEnum.LOADING);
		} else if (!!data && isExportEnabled) {
			setExportPolicyState(ExportPolicyEnum.ENABLED);
		} else if (!!data && !isExportEnabled) {
			setExportPolicyState(ExportPolicyEnum.DISABLED);
		}
	}, [data, loading, error]);

	return { exportPolicyState, error };
};
